import { defineComponent as _defineComponent } from 'vue'
import { vShow as _vShow, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { onMounted, onUnmounted, inject, ref, Ref } from 'vue';
import services from '@/components/cookies/services';
import * as Klaro from 'klaro';
import events from '@/events';

const cookieName = 'klaro';

export default /*#__PURE__*/_defineComponent({
  __name: 'CookieManager',
  props: {
    kicker: {},
    title: {},
    description: {},
    privacyPolicyLink: {},
    privacyPolicyLabel: {},
    privacyPolicyDescription: {},
    essentialLabel: {},
    functionalLabel: {},
    marketingLabel: {},
    serviceLabel: {},
    servicesLabel: {},
    klaroDescription: {},
    recaptchaDescription: {},
    youtubeDescription: {},
    googleMapsDescription: {},
    tagManagerDescription: {},
    googleAnalyticsDescription: {},
    hotjarDescription: {},
    acceptAllLabel: {},
    acceptSelectedLabel: {}
  },
  setup(__props: any) {

const props = __props;



const klaroContainer: Ref<HTMLElement> = ref(null);
const contextPath = inject('contextPath');
const visible = ref(false);
let klaroConfig: { [key: string]: any } = {};
let initialCookie: string | undefined;

onUnmounted(() => {
    events.$off('enable-cookies', enableCookies);
    events.$off('show', show);
});

onMounted(() => {
    klaroConfig = configure();
    Klaro.setup(klaroConfig);
    dispatchConsents(true);
    events.$on('enable-cookies', enableCookies);
    events.$on('show', show);
    show(500);
});

function show(delay = 100) {
    Klaro.show(klaroConfig);
    setTimeout(() => {
        // add acceptAll button and arrow icon
        const footer = klaroContainer.value.getElementsByClassName('cm-footer-buttons')[0];
        if (footer) {
            // create arrow icon
            const svgElem = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            const useElem = document.createElementNS('http://www.w3.org/2000/svg', 'use');
            svgElem.classList.add(
                'ml-2',
                'transition-all',
                'w-12',
                'h-6',
                'stroke-3',
                'stroke-purple',
                'group-hover:translate-x-2',
                'transition-transform'
            );
            useElem.setAttributeNS(
                'http://www.w3.org/1999/xlink',
                'xlink:href',
                contextPath + '/.resources/rize/webresources/img/sprite.svg#arrow'
            );
            useElem.setAttribute('style', 'pointer-events: none;');
            svgElem.appendChild(useElem);

            // create acceptAll button and append arrow icon
            const acceptAllButton = document.createElement('button');
            acceptAllButton.textContent = props.acceptAllLabel;
            acceptAllButton.classList.add('cm-btn', 'cm-btn-success', 'cm-btn-accept-all');
            acceptAllButton.classList.add('group');
            acceptAllButton.append(svgElem);
            acceptAllButton.addEventListener('click', acceptAll);

            footer.append(acceptAllButton);
        }

        // add kicker
        const title = klaroContainer.value.getElementsByClassName('cm-header')[0];
        if (title) {
            const kickerSpan = document.createElement('div');
            kickerSpan.textContent = props.kicker;
            kickerSpan.classList.add(
                'kicker',
                'mb-2',
                'text-base',
                'font-normal',
                'order-1',
                'text-black-900'
            );

            const kickerDiv = document.createElement('div');
            kickerDiv.classList.add('flex', 'relative', 'top-1');
            kickerDiv.append(kickerSpan);
            title.append(kickerDiv);
        }

        // append invisible function title to position cm-caret correctly
        const purposes = klaroContainer.value.getElementsByClassName('cm-purpose');
        if (purposes) {
            for (const purpose of purposes) {
                const purposeTitle = purpose.getElementsByClassName('cm-list-title')[0];
                const purposeCaret = purpose.getElementsByClassName('cm-caret')[0];
                if (purposeTitle && purposeCaret) {
                    const invisibleTitle = document.createElement('span');
                    invisibleTitle.textContent = purposeTitle.textContent;
                    invisibleTitle.classList.add(
                        'order-1',
                        'text-md',
                        'pr-2',
                        'invisible'
                    );
                    purposeCaret.appendChild(invisibleTitle);
                }
            }
        }

        // add listeners after adding buttons
        // necessary since klaro displays different buttons when the 'klaro' cookie is set
        registerSaveHandler(klaroContainer.value);
        visible.value = true;
    }, delay);
}

function registerSaveHandler(container: HTMLElement) {
    requestAnimationFrame(() => {
        container.querySelectorAll('.cm-btn').forEach((btn: Element) => {
            // remove listener before adding it again to prevent duplicates
            btn.removeEventListener('click', () => dispatchConsents());
            btn.addEventListener('click', () => dispatchConsents());
        });
    });
}

function dispatchConsents(initial = false) {
    visible.value = false;
    dispatchFrom(Klaro.getManager(klaroConfig)?.consents);
    if (!!initialCookie && !initial && !(initialCookie === getCookie())) {
        deleteAllCookies();
        window.location.reload();
    }
    initialCookie = getCookie();
}

function dispatchFrom(obj: { [key: string]: boolean }) {
    Object.keys(obj).forEach((key: string) => {
        window.dispatchEvent(
            new CustomEvent(`consent-${key}`, {
                detail: { consent: obj[key], service: key }
            })
        );
    });
}

function deleteAllCookies() {
    const cookies = document.cookie.split(';');
    cookies.forEach(v => {
        const eqPos = v.indexOf('=');
        const name = eqPos > -1 ? v.substr(0, eqPos) : v;
        if (name.trim() !== cookieName) {
            document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
        }
    });
}

function getCookie(): string | undefined {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${cookieName}=`);
    if (parts.length === 2) {
        return parts?.pop()?.split(';').shift();
    }
}

function enableCookies(service: string) {
    const manager = Klaro.getManager(klaroConfig);
    manager.updateConsent(service, true);
    manager.saveAndApplyConsents();
    dispatchConsents();
}

function acceptAll() {
    const manager = Klaro.getManager(Klaro.klaroConfig);
    manager.changeAll(true);
    manager.saveAndApplyConsents();
    dispatchConsents();
}

function configure(): { [key: string]: any } {
    const w: any = window as any;
    w.klaroConfig = {
        activeConfig: '',
        testing: false,
        elementID: 'klaro',
        storageMethod: 'cookie',
        storageName: cookieName,
        htmlTexts: true,
        cookieExpiresAfterDays: 30,
        default: false,
        mustConsent: true,
        acceptAll: false,
        hideDeclineAll: true,
        hideLearnMore: true,
        groupByPurpose: true,
        disablePoweredBy: true,
        lang: 'en',
        languages: ['en'],
        translations: {
            en: {
                privacyPolicyUrl: props.privacyPolicyLink,
                consentModal: {
                    title: props.title,
                    description: props.description,
                    service: props.serviceLabel
                },
                acceptAll: props.acceptAllLabel,
                acceptSelected: props.acceptSelectedLabel,
                save: props.acceptSelectedLabel,
                decline: 'Decline',
                service: {
                    required: {
                        title: 'Service Required',
                        description: 'Service Required Description'
                    }
                },
                privacyPolicy: {
                    name: props.privacyPolicyLabel,
                    text: props.privacyPolicyDescription
                },
                purposes: {
                    essential: {
                        title: props.essentialLabel,
                        description: ''
                    },
                    functional: {
                        title: props.functionalLabel,
                        description: ''
                    },
                    marketing: {
                        title: props.marketingLabel,
                        description: ''
                    }
                },
                purposeItem: {
                    service: props.serviceLabel,
                    services: props.servicesLabel
                }
            }
        },
        services: [
            {
                name: services.klaro,
                default: true,
                required: true,
                optOut: false,
                onlyOnce: true,
                purposes: ['essential'],
                cookies: [
                    [/^_pk_.*$/, '/', 'klaro.kiprotect.com'],
                    [/^_pk_.*$/, '/', 'localhost'],
                    [/^_pk_.*$/, '/', 'lz-webhub-demo.grapefruit.infra.lemonize.io'],
                    [/^_pk_.*$/, '/', 'rize.cloud'],
                    'piwik_ignore'
                ],
                translations: {
                    zz: {
                        title: 'Klaro',
                        description: props.klaroDescription
                    }
                }
            },
            {
                name: services.recaptcha,
                default: true,
                required: true,
                optOut: false,
                onlyOnce: true,
                purposes: ['essential'],
                cookies: [
                    [/^_pk_.*$/, '/', 'klaro.kiprotect.com'],
                    [/^_pk_.*$/, '/', 'localhost'],
                    [/^_pk_.*$/, '/', 'lz-webhub-demo.grapefruit.infra.lemonize.io'],
                    [/^_pk_.*$/, '/', 'rize.cloud'],
                    'piwik_ignore'
                ],
                translations: {
                    zz: {
                        title: 'reCAPTCHA',
                        description: props.recaptchaDescription
                    }
                }
            },
            {
                name: services.youtube,
                default: false,
                required: false,
                optOut: false,
                onlyOnce: true,
                purposes: ['functional'],
                cookies: [
                    [/^_pk_.*$/, '/', 'klaro.kiprotect.com'],
                    [/^_pk_.*$/, '/', 'localhost'],
                    [/^_pk_.*$/, '/', 'lz-webhub-demo.grapefruit.infra.lemonize.io'],
                    [/^_pk_.*$/, '/', 'rize.cloud'],
                    'piwik_ignore'
                ],
                translations: {
                    zz: {
                        title: 'Youtube',
                        description: props.youtubeDescription
                    }
                }
            },
            {
                name: services.googleMaps,
                default: false,
                required: false,
                optOut: false,
                onlyOnce: true,
                purposes: ['functional'],
                cookies: [
                    [/^_pk_.*$/, '/', 'klaro.kiprotect.com'],
                    [/^_pk_.*$/, '/', 'localhost'],
                    [/^_pk_.*$/, '/', 'lz-webhub-demo.grapefruit.infra.lemonize.io'],
                    [/^_pk_.*$/, '/', 'rize.cloud'],
                    'piwik_ignore'
                ],
                translations: {
                    zz: {
                        title: 'Google Maps',
                        description: props.googleMapsDescription
                    }
                }
            },
            {
                name: services.tagManager,
                default: true,
                required: true,
                optOut: false,
                onlyOnce: true,
                purposes: ['essential'],
                cookies: [
                    [/^_pk_.*$/, '/', 'klaro.kiprotect.com'],
                    [/^_pk_.*$/, '/', 'localhost'],
                    [/^_pk_.*$/, '/', 'lz-webhub-demo.grapefruit.infra.lemonize.io'],
                    [/^_pk_.*$/, '/', 'rize.cloud'],
                    'piwik_ignore'
                ],
                translations: {
                    zz: {
                        title: 'Tag Manager',
                        description: props.tagManagerDescription
                    }
                },
                onAccept: `
                    window.setTimeout(function() {
                        for (let k of Object.keys(opts.consents)) {
                            if (opts.consents[k]) {
                                let eventName = 'klaro-' + k + '-accepted'
                                dataLayer.push({ event: eventName })
                            }
                        }
                    }, 0)
                `,
                onInit: `
                    window.dataLayer = window.dataLayer || [];
                    window.gtag = function(){dataLayer.push(arguments)}
                    gtag('consent', 'default', {'ad_storage': 'denied', 'analytics_storage': 'denied', 'ad_user_data': 'denied', 'ad_personalization': 'denied', 'wait_for_update': 2000})
                    gtag('set', 'ads_data_redaction', true)
                `
            },
            {
                name: services.googleAnalytics,
                default: false,
                required: false,
                optOut: false,
                onlyOnce: true,
                purposes: ['marketing'],
                cookies: [
                    [/^_pk_.*$/, '/', 'klaro.kiprotect.com'],
                    [/^_pk_.*$/, '/', 'localhost'],
                    [/^_pk_.*$/, '/', 'lz-webhub-demo.grapefruit.infra.lemonize.io'],
                    [/^_pk_.*$/, '/', 'rize.cloud'],
                    'piwik_ignore',
                    /^_ga(_.*)?/
                ],
                translations: {
                    zz: {
                        title: 'Google Analytics',
                        description: props.googleAnalyticsDescription
                    }
                },
                onAccept: `
                    gtag('consent', 'update', {
                        'analytics_storage': 'granted',
                    })
                `,
                onDecline: `
                    gtag('consent', 'update', {
                        'analytics_storage': 'denied',
                    })
                `
            },
            {
                name: services.hotjar,
                default: false,
                required: false,
                optOut: false,
                onlyOnce: true,
                purposes: ['marketing'],
                cookies: [
                    [/^_pk_.*$/, '/', 'klaro.kiprotect.com'],
                    [/^_pk_.*$/, '/', 'localhost'],
                    [/^_pk_.*$/, '/', 'lz-webhub-demo.grapefruit.infra.lemonize.io'],
                    [/^_pk_.*$/, '/', 'rize.cloud'],
                    'piwik_ignore'
                ],
                translations: {
                    zz: {
                        title: 'Hotjar',
                        description: props.hotjarDescription
                    }
                }
            }
        ]
    };

    return w.klaroConfig;
}

return (_ctx: any,_cache: any) => {
  return _withDirectives((_openBlock(), _createElementBlock("div", {
    id: "klaro",
    ref_key: "klaroContainer",
    ref: klaroContainer
  }, null, 512)), [
    [_vShow, visible.value]
  ])
}
}

})